import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UploadCloud, FileSpreadsheet, Download, CheckCircle2, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const norm = (s) => String(s ?? "").toLowerCase().trim();

function pickField(row, matchers) {
  for (const key of Object.keys(row)) {
    const nk = norm(key);
    if (matchers.some((m) => nk.includes(m))) {
      const val = row[key];
      if (val !== "" && val !== null && val !== undefined) return val;
    }
  }
  return "";
}

function parseNum(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v;
  let s = String(v).replace(/\s/g, "").replace(/kr/gi, "");
  // Normalise Norwegian formatting: 1.234.567,89 -> 1234567.89
  if (s.includes(",") && s.includes(".")) s = s.replace(/\./g, "").replace(/,/g, ".");
  else if (s.includes(",")) s = s.replace(/,/g, ".");
  const n = parseFloat(s.replace(/[^0-9.\-]/g, ""));
  return isNaN(n) ? null : n;
}

const statusByLabel = {
  prospekt: "prospect",
  "innledende": "initial_contact",
  "interessert": "interested",
  "sendt info": "info_sent",
  evaluering: "evaluation",
  intensjon: "letter_of_intent",
  forhandling: "negotiation_dd",
  gjennomf: "closed_won",
};
const validStatuses = ["prospect", "initial_contact", "interested", "info_sent", "evaluation", "letter_of_intent", "negotiation_dd", "closed_won"];
function parseStatus(v) {
  const nv = norm(v);
  if (!nv) return "prospect";
  if (validStatuses.includes(nv)) return nv;
  for (const [k, val] of Object.entries(statusByLabel)) if (nv.includes(k)) return val;
  return "prospect";
}

function rowToCandidate(row) {
  const price = parseNum(pickField(row, ["pris", "price", "verdi"]));
  const ebitda = parseNum(pickField(row, ["ebitda", "driftsresultat"]));
  let multiple = parseNum(pickField(row, ["multipel", "multiple"]));
  if (!multiple && price && ebitda) multiple = Math.round((price / ebitda) * 10) / 10;
  return {
    company_name: String(pickField(row, ["bedriftsnavn", "firmanavn", "selskap", "company", "bedrift"]) || "").trim(),
    contact_person: String(pickField(row, ["kontaktperson", "kontakt", "contact"]) || "").trim(),
    contact_email: String(pickField(row, ["e-post", "epost", "email", "mail"]) || "").trim(),
    contact_phone: String(pickField(row, ["telefon", "phone", "tlf", "mobil"]) || "").trim(),
    industry: String(pickField(row, ["bransje", "industry", "sektor"]) || "").trim(),
    revenue: parseNum(pickField(row, ["omsetning", "revenue"])),
    ebitda,
    discussed_price: price,
    multiple,
    employees: parseNum(pickField(row, ["ansatte", "employees"])),
    status: parseStatus(pickField(row, ["status"])),
    notes: String(pickField(row, ["notat", "notes", "kommentar", "merknad"]) || "").trim(),
  };
}

export default function ImportCandidatesDialog({ open, onOpenChange, customer, onImport, isImporting }) {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setFileName(file.name);
    try {
      const isCsv = /\.csv$/i.test(file.name);
      let wb;
      if (isCsv) {
        const text = await file.text();
        wb = XLSX.read(text, { type: "string" });
      } else {
        const buf = await file.arrayBuffer();
        wb = XLSX.read(buf, { type: "array" });
      }
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const parsed = raw.map(rowToCandidate).filter((c) => c.company_name);
      if (parsed.length === 0) {
        setError("Fant ingen rader med bedriftsnavn. Sjekk at filen har en kolonne for bedriftsnavn.");
        setRows([]);
        return;
      }
      setRows(parsed);
    } catch (e) {
      setError("Kunne ikke lese filen. Støttede formater: .xlsx, .xls, .csv");
      setRows([]);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const downloadTemplate = () => {
    const headers = ["Bedriftsnavn", "Kontaktperson", "E-post", "Telefon", "Bransje", "Omsetning", "EBITDA", "Pris", "Ansatte", "Status", "Notater"];
    const example = ["Eksempel Regnskap AS", "Per Hansen", "per@eksempel.no", "+47 900 00 000", "consulting", 25000000, 3500000, 15000000, 18, "prospect", "Interessant kandidat"];
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Kandidater");
    XLSX.writeFile(wb, "kandidat_mal.xlsx");
  };

  const reset = () => {
    setRows([]);
    setFileName("");
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const confirmImport = () => {
    if (rows.length === 0) return;
    onImport(rows);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand-ink">
            <FileSpreadsheet className="w-5 h-5 text-brand-limeDark" />
            Importer kandidater fra Excel / CSV
          </DialogTitle>
          <DialogDescription>
            Kandidatene knyttes til <span className="font-semibold text-brand-ink">{customer?.company_name}</span>.
          </DialogDescription>
        </DialogHeader>

        {rows.length === 0 ? (
          <div className="space-y-4">
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className="cursor-pointer rounded-2xl border-2 border-dashed border-slate-200 hover:border-brand-limeDark hover:bg-brand-limeSoft/40 transition-colors p-10 flex flex-col items-center justify-center text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-brand-limeSoft flex items-center justify-center mb-3">
                <UploadCloud className="w-7 h-7 text-brand-limeDark" />
              </div>
              <p className="font-semibold text-brand-ink">Slipp filen her, eller klikk for å velge</p>
              <p className="text-sm text-slate-400 mt-1">Støtter .xlsx, .xls og .csv</p>
              {fileName && <p className="text-xs text-slate-500 mt-3">Valgt: {fileName}</p>}
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
              <p className="text-sm text-slate-500">Vet du ikke hvordan filen skal se ut?</p>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="w-4 h-4 mr-2" /> Last ned mal
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-xl p-3">
              <CheckCircle2 className="w-4 h-4" />
              Fant <span className="font-semibold">{rows.length}</span> kandidater i <span className="font-semibold">{fileName}</span>
            </div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-slate-500">
                      <th className="px-3 py-2 font-medium">Bedrift</th>
                      <th className="px-3 py-2 font-medium">Kontakt</th>
                      <th className="px-3 py-2 font-medium">Bransje</th>
                      <th className="px-3 py-2 font-medium text-right">Omsetning</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r, i) => (
                      <tr key={i} className="text-slate-700">
                        <td className="px-3 py-2 font-medium text-brand-ink">{r.company_name}</td>
                        <td className="px-3 py-2">{r.contact_person || "—"}</td>
                        <td className="px-3 py-2">{r.industry || "—"}</td>
                        <td className="px-3 py-2 text-right">{r.revenue ? `${(r.revenue / 1e6).toFixed(1)}M` : "—"}</td>
                        <td className="px-3 py-2"><Badge variant="secondary" className="font-normal">{r.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <button onClick={reset} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600 transition-colors">
              <Trash2 className="w-4 h-4" /> Velg en annen fil
            </button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Avbryt</Button>
          <Button onClick={confirmImport} disabled={rows.length === 0 || isImporting}>
            {isImporting ? "Importerer..." : `Importer ${rows.length || ""} kandidater`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
